import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { SideMenuPageContainer } from "../../components/side_menu_page_container/SideMenuPageContainer";
import Grid2 from "@mui/material/Unstable_Grid2/Grid2";
import {
  firstNameChanged,
  lastNameChanged,
  emailChanged,
  phoneChanged,
  websiteChanged,
  linkedinChanged,
  styleChanged,
  logoPositionChanged,
  includeLogoChanged,
  iconStyleChanged,
  signatureColorChanged,
  logoUploaded,
  logoRemoved,
  signatureGenerated,
  generationStarted,
  generationFailed,
  clearError,
  resetGenerator,
  validateEmail,
  validatePhone,
  validateUrl,
  validateLogoFile,
  generateSignatureHTML,
  SignatureStyle,
  LogoPosition,
  IconStyle,
  selectState,
} from "./EmailSignatureGeneratorSlice";
import {
  Box,
  Divider,
  MenuItem,
  Stack,
  TextField,
  Typography,
  Alert,
  IconButton,
  Tooltip,
  Paper,
  Switch,
  FormControlLabel,
  InputAdornment,
} from "@mui/material";
import { CustomSelect } from "../../components/custom_select/CustomSelect";
import { CustomButton } from "../../components/custom_button/CustomButton";
import UploadIcon from "@mui/icons-material/Upload";
import EmailIcon from "@mui/icons-material/AlternateEmailOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopyOutlined";
import DownloadIcon from "@mui/icons-material/Download";
import ClearIcon from "@mui/icons-material/Clear";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { HomeCard } from "../../components/home_card/HomeCard";
import QrCodeGeneratorImage from "../../assets/QrCodeGenerator.webp";
import BarcodeGeneratorImage from "../../assets/BarcodeGenerator.webp";
import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import AdSense from "../../components/adsense/AdSense";
import { ADSENSE_SLOTS } from "../../config/adsense";

export function EmailSignatureGenerator() {
  const dispatch = useAppDispatch();
  const state = useAppSelector(selectState);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});
  const { t } = useTranslation();

  const validateForm = useCallback(() => {
    const errors: Record<string, string> = {};

    const emailError = validateEmail(state.email);
    if (emailError) errors.email = emailError;

    const phoneError = validatePhone(state.phone);
    if (phoneError) errors.phone = phoneError;

    const websiteError = validateUrl(state.website);
    if (websiteError) errors.website = websiteError;

    const linkedinError = validateUrl(state.linkedin);
    if (linkedinError) errors.linkedin = linkedinError;

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [state.email, state.phone, state.website, state.linkedin]);

  const handleGenerateSignature = useCallback(() => {
    if (!validateForm()) {
      dispatch(
        generationFailed(
          t("emailSignatureGenerator.errors.fixValidation")
        )
      );
      return;
    }

    dispatch(generationStarted());

    try {
      const signatureHTML = generateSignatureHTML(state);

      if (!signatureHTML) {
        dispatch(
          generationFailed(
            t("emailSignatureGenerator.errors.provideOne")
          )
        );
        return;
      }

      dispatch(signatureGenerated(signatureHTML));
    } catch (error) {
      dispatch(
        generationFailed(
          error instanceof Error
            ? error.message
            : t("emailSignatureGenerator.errors.failedGenerate")
        )
      );
    }
  }, [state, dispatch, validateForm, t]);

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];

      const validationError = validateLogoFile(file);
      if (validationError) {
        dispatch(generationFailed(validationError));
        return;
      }

      const reader = new FileReader();
      reader.addEventListener("load", () => {
        dispatch(
          logoUploaded({
            dataUrl: reader.result as string,
            fileName: file.name,
          })
        );
      });
      reader.readAsDataURL(file);
    }
  };

  const handleCopySignature = async () => {
    if (state.generatedSignature) {
      try {
        await navigator.clipboard.writeText(state.generatedSignature);
      } catch (error) {
        console.error("Failed to copy signature:", error);
      }
    }
  };

  const handleDownloadSignature = () => {
    if (state.generatedSignature) {
      const blob = new Blob([state.generatedSignature], { type: "text/html" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "email_signature.html";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  const handleReset = () => {
    dispatch(resetGenerator());
    setValidationErrors({});
  };

  return (
    <SideMenuPageContainer
      pageTitle={t("emailSignatureGenerator.title")}
      pageSeoTitle={t("emailSignatureGenerator.seoTitle")}
      pageDescription={t("emailSignatureGenerator.description")}
    >
      <Box sx={{ width: { xs: 300, sm: 350, md: 900, lg: 1200 } }}>
        <Grid2
          container
          spacing={2}
          sx={{ mt: 2 }}
          columns={{ xs: 6, sm: 6, md: 12, lg: 12 }}
        >
          <Grid2 xs={12}>
            <Typography variant="h2" sx={{ mb: 2 }}>
              {t("emailSignatureGenerator.mainHeading")}
            </Typography>
          </Grid2>

          {/* Input Section */}
          <Grid2 xs={6} md={6}>
            <Paper sx={{ p: 3, mb: 2, background: "#2e303200", borderColor: "#282B39", borderStyle: "dashed"}}>
              <Typography variant="h4" sx={{ mb: 3 }}>
                {t("emailSignatureGenerator.personalInformation")}
              </Typography>

              <Stack spacing={2}>
                <TextField
                  label={t("emailSignatureGenerator.firstName")}
                  value={state.firstName}
                  onChange={(e) => dispatch(firstNameChanged(e.target.value))}
                  placeholder={t("emailSignatureGenerator.placeholders.firstName")}
                />
                <TextField
                  label={t("emailSignatureGenerator.lastName")}
                  value={state.lastName}
                  onChange={(e) => dispatch(lastNameChanged(e.target.value))}
                  placeholder={t("emailSignatureGenerator.placeholders.lastName")}
                />

                <TextField
                  label={t("emailSignatureGenerator.emailAddress")}
                  value={state.email}
                  onChange={(e) => dispatch(emailChanged(e.target.value))}
                  placeholder={t("emailSignatureGenerator.placeholders.email")}
                  error={!!validationErrors.email}
                  helperText={validationErrors.email ? t("emailSignatureGenerator.validation.emailInvalid") : undefined}
                />

                <TextField
                  label={t("emailSignatureGenerator.phoneNumber")}
                  value={state.phone}
                  onChange={(e) => dispatch(phoneChanged(e.target.value))}
                  placeholder={t("emailSignatureGenerator.placeholders.phone")}
                  error={!!validationErrors.phone}
                  helperText={validationErrors.phone ? t("emailSignatureGenerator.validation.phoneInvalid") : undefined}
                />

                <TextField
                  label={t("emailSignatureGenerator.website")}
                  value={state.website}
                  onChange={(e) => dispatch(websiteChanged(e.target.value))}
                  placeholder={t("emailSignatureGenerator.placeholders.website")}
                  error={!!validationErrors.website}
                  helperText={validationErrors.website ? t("emailSignatureGenerator.validation.urlInvalid") : undefined}
                />

                <TextField
                  label={t("emailSignatureGenerator.linkedinProfile")}
                  value={state.linkedin}
                  onChange={(e) => dispatch(linkedinChanged(e.target.value))}
                  placeholder={t("emailSignatureGenerator.placeholders.linkedin")}
                  error={!!validationErrors.linkedin}
                  helperText={validationErrors.linkedin ? t("emailSignatureGenerator.validation.urlInvalid") : undefined}
                />
              </Stack>
            </Paper>
            </Grid2>
            <Grid2 xs={6} md={6}>
            <Paper sx={{ p: 3, mb: 2, background: "#2e303200", borderColor: "#282B39", borderStyle: "dashed" }}>
              <Typography variant="h4" sx={{ mb: 3 }}>
                {t("emailSignatureGenerator.signatureStyle")}
              </Typography>

              <Stack spacing={2}>
                <CustomSelect
                  label={t("emailSignatureGenerator.style")}
                  value={state.style}
                  onChange={(e) =>
                    dispatch(styleChanged(e.target.value as SignatureStyle))
                  }
                >
                  <MenuItem value={SignatureStyle.MODERN}>{t("emailSignatureGenerator.styles.modern")}</MenuItem>
                  <MenuItem value={SignatureStyle.CLASSIC}>{t("emailSignatureGenerator.styles.classic")}</MenuItem>
                  <MenuItem value={SignatureStyle.MINIMAL}>{t("emailSignatureGenerator.styles.minimal")}</MenuItem>
                  <MenuItem value={SignatureStyle.CORPORATE}>
                    {t("emailSignatureGenerator.styles.corporate")}
                  </MenuItem>
                </CustomSelect>

                <CustomSelect
                  label={t("emailSignatureGenerator.iconStyle")}
                  value={state.iconStyle}
                  onChange={(e) =>
                    dispatch(iconStyleChanged(e.target.value as IconStyle))
                  }
                >
                  <MenuItem value={IconStyle.EMOJIS}>{t("emailSignatureGenerator.iconStyles.emojis")}</MenuItem>
                  <MenuItem value={IconStyle.MATERIAL_ICONS}>{t("emailSignatureGenerator.iconStyles.materialIcons")}</MenuItem>
                  <MenuItem value={IconStyle.NONE}>{t("emailSignatureGenerator.iconStyles.none")}</MenuItem>
                </CustomSelect>

                <TextField
                  label={t("emailSignatureGenerator.signatureColor")}
                  type="color"
                  value={state.signatureColor}
                  onChange={(e) => dispatch(signatureColorChanged(e.target.value))}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Box
                          sx={{
                            width: 20,
                            height: 20,
                            borderRadius: 1,
                            backgroundColor: state.signatureColor,
                            border: '1px solid #ccc',
                          }}
                        />
                      </InputAdornment>
                    ),
                  }}
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={state.includeLogo}
                      onChange={(e) =>
                        dispatch(includeLogoChanged(e.target.checked))
                      }
                      color="primary"
                    />
                  }
                  label={t("emailSignatureGenerator.includeLogo")}
                />

                {state.includeLogo && (
                  <>
                    <CustomSelect
                      label={t("emailSignatureGenerator.logoPosition")}
                      value={state.logoPosition}
                      onChange={(e) =>
                        dispatch(
                          logoPositionChanged(e.target.value as LogoPosition)
                        )
                      }
                    >
                      <MenuItem value={LogoPosition.LEFT}>{t("emailSignatureGenerator.positions.left")}</MenuItem>
                      <MenuItem value={LogoPosition.RIGHT}>{t("emailSignatureGenerator.positions.right")}</MenuItem>
                      <MenuItem value={LogoPosition.TOP}>{t("emailSignatureGenerator.positions.top")}</MenuItem>
                      <MenuItem value={LogoPosition.BOTTOM}>{t("emailSignatureGenerator.positions.bottom")}</MenuItem>
                    </CustomSelect>

                    <Box>
                      <input
                        id="logo-upload"
                        type="file"
                        accept="image/*"
                        onChange={handleLogoUpload}
                        style={{ display: "none" }}
                      />
                      <label htmlFor="logo-upload">
                        <CustomButton
                          icon={<UploadIcon sx={{ color: "#FFD9A8" }} />}
                          label={t("emailSignatureGenerator.uploadLogo")}
                          onClick={() =>
                            document.getElementById("logo-upload")?.click()
                          }
                        />
                      </label>
                    </Box>

                    {state.logoDataUrl && (
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 2 }}
                      >
                        <img
                          src={state.logoDataUrl}
                          alt="Logo"
                          style={{ maxWidth: "100px", maxHeight: "50px" }}
                        />
                        <Typography variant="body2" color="textSecondary">
                          {state.logoFileName}
                        </Typography>
                        <Tooltip title={t("emailSignatureGenerator.removeLogo")}>
                          <IconButton
                            onClick={() => dispatch(logoRemoved())}
                            color="secondary"
                            size="small"
                          >
                            <ClearIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}


                  </>
                )}
              </Stack>
            </Paper>
          </Grid2>

          <Grid2 xs={12}>
            <Stack direction="row" spacing={2}>
              <CustomButton
                icon={<EmailIcon sx={{ color: "#FFD9A8" }} />}
                label={
                  state.isGenerating ? t("emailSignatureGenerator.actions.generating") : t("emailSignatureGenerator.actions.generate")
                }
                onClick={handleGenerateSignature}
                disabled={state.isGenerating}
              />

              <Tooltip title={t("emailSignatureGenerator.actions.reset")}>
                <IconButton onClick={handleReset} color="secondary">
                  <RestartAltIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          </Grid2>

          {/* Preview Section */}
          <Grid2 xs={12}>
            <Paper sx={{ p: 3, mb: 2 }}>
              <Typography variant="h4" sx={{ mb: 3 }}>
                {t("emailSignatureGenerator.preview")}
              </Typography>

              {state.error && (
                <Alert
                  severity="error"
                  onClose={() => dispatch(clearError())}
                  sx={{ mb: 2 }}
                >
                  {state.error}
                </Alert>
              )}

              {state.generatedSignature ? (
                <Box>
                  <Box
                    sx={{
                      border: "1px solid #ddd",
                      borderRadius: 1,
                      p: 2,
                      mb: 2,
                      backgroundColor: "#fff",
                      minHeight: "200px",
                      overflowX: "auto",
                      overflowY: "hidden",
                      "& > div": {
                        minWidth: "400px",
                      },
                    }}
                    dangerouslySetInnerHTML={{
                      __html: state.generatedSignature,
                    }}
                  />

                  <Stack 
                    direction={{ xs: "column", sm: "row" }} 
                    spacing={2}
                    sx={{
                      "& .MuiButton-root": {
                        minWidth: { xs: "100%", sm: "auto" },
                      },
                    }}
                  >
                    <CustomButton
                      icon={<ContentCopyIcon sx={{ color: "#FFD9A8" }} />}
                      label={t("emailSignatureGenerator.actions.copyHtml")}
                      onClick={handleCopySignature}
                    />
                    <CustomButton
                      icon={<DownloadIcon sx={{ color: "#FFD9A8" }} />}
                      label={t("emailSignatureGenerator.actions.download")}
                      onClick={handleDownloadSignature}
                    />
                  </Stack>
                </Box>
              ): (
                <Box
                  sx={{
                    border: "1px solid #ddd",
                    borderRadius: 1,
                    p: 2,
                    backgroundColor: "#f9f9f9",
                    minHeight: "200px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography variant="body1" color="textSecondary">
                    {t("emailSignatureGenerator.previewPlaceholder")}
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid2>

          <Grid2 xs={12} sx={{ mt: 3, mb: 3 }}>
            <Divider sx={{ backgroundColor: "#7895a1" }}></Divider>
          </Grid2>

          {/* SEO Content */}
          <Grid2 xs={12}>
            <AdSense
                adSlot={ADSENSE_SLOTS.IN_ARTICLE}
                adFormat="fluid"
                adLayoutKey="-fb+5w+4e-db+86"
                sx={{ mb: 3 }}
              />
            <Typography variant="h3" sx={{ mb: 2 }}>
              {t("emailSignatureGenerator.content.freeHeading")}
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              {t("emailSignatureGenerator.content.freeDescription")}
            </Typography>

            <Typography variant="h3" sx={{ mb: 2 }}>
              {t("emailSignatureGenerator.content.stylesHeading")}
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              {t("emailSignatureGenerator.content.stylesDescription")}
            </Typography>

            <Typography variant="h3" sx={{ mb: 2 }}>
              {t("emailSignatureGenerator.content.customizeHeading")}
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              {t("emailSignatureGenerator.content.customizeDescription")}
            </Typography>

            <Typography variant="h3" sx={{ mb: 2 }}>
              {t("emailSignatureGenerator.content.logoHeading")}
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              {t("emailSignatureGenerator.content.logoDescription")}
            </Typography>

            <Typography variant="h3" sx={{ mb: 2 }}>
              {t("emailSignatureGenerator.content.easyHeading")}
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              {t("emailSignatureGenerator.content.easyDescription")}
            </Typography>
          </Grid2>

          <Grid2 xs={12}>
            <Typography variant="h3" sx={{ mt: 3, mb: 1 }}>
              {t("emailSignatureGenerator.related")}
            </Typography>
          </Grid2>

          <Grid2 xs={6}>
            <HomeCard
              languageResource="qrCodeGenerator"
              link="/QrCodeGenerator"
              smallCard={true}
              image={QrCodeGeneratorImage}
            />
          </Grid2>
          <Grid2 xs={6}>
            <HomeCard
              languageResource="barcodeGenerator"
              link="/BarcodeGenerator"
              smallCard={true}
              image={BarcodeGeneratorImage}
            />
          </Grid2>
        </Grid2>
      </Box>
    </SideMenuPageContainer>
  );
}
